// src/components/Drive/AdminPanels/CreatePanel.jsx
import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Send,
  PlusCircle,
  Type,
  Image as ImageIcon,
  Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { useDeliveryDraft } from "../../../shared/hooks/useDeliveryDraft";
import {
  parseDeliveryTextFn,
  parseDeliveryAudioFn,
  parseDeliveryImageFn,
} from "../../../firebase/deliveryFunctions";

/* ── helpers ── */

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function backendToDraft(d) {
  const now = new Date();
  const fb = (min) => new Date(now.getTime() + min * 60000).toISOString();

  return {
    origin: d.from || "",
    destination: d.to || "",
    originData: d.fromInfo?.latitude ? { address: d.fromInfo.address, latitude: d.fromInfo.latitude, longitude: d.fromInfo.longitude, placeId: d.fromInfo.placeId, city: d.fromInfo.city, postalCode: d.fromInfo.postalCode } : null,
    destData: d.toInfo?.latitude ? { address: d.toInfo.address, latitude: d.toInfo.latitude, longitude: d.toInfo.longitude, placeId: d.toInfo.placeId, city: d.toInfo.city, postalCode: d.toInfo.postalCode } : null,
    distance: null,
    duration: null,
    items: [{ id: Date.now(), weight: d.weightKg || 5, dimensions: { length: 30, width: 20, height: 15 }, sizeCategory: d.packageSize || "small" }],
    selectedSize: d.packageSize || "small",
    weightKg: d.weightKg || 5,
    pickupTime: d.pickUpTime || fb(30),
    targetTime: d.targetTime || fb(180),
    hasTimeWindow: d.hasTimeWindow || false,
    latestPickupTime: d.latestPickupTime || fb(90),
    earliestTargetTime: d.earliestTargetTime || fb(150),
    pickupApartment: d.fromInfo?.details?.apartment || "",
    pickupFloor: d.fromInfo?.details?.floor || "",
    pickupBuilding: d.fromInfo?.details?.building || "",
    pickupAccessCode: d.fromInfo?.details?.accessCode || "",
    pickupInstructions: d.fromInfo?.details?.instructions || "",
    senderName: d.senderInfo?.name || "",
    senderPhone: d.senderPhone || "",
    senderEmail: d.senderInfo?.email || "",
    deliveryApartment: d.toInfo?.details?.apartment || "",
    deliveryFloor: d.toInfo?.details?.floor || "",
    deliveryBuilding: d.toInfo?.details?.building || "",
    deliveryAccessCode: d.toInfo?.details?.accessCode || "",
    deliveryInstructions: d.toInfo?.details?.instructions || "",
    recipientName: d.receiverInfo?.name || "",
    recipientPhone: d.receiverPhone || "",
    recipientEmail: d.receiverInfo?.email || "",
    returnParcels: d.returnParcels || false,
    temperatureControl: d.temperatureControl || false,
    canBeLeftAtDoor: d.canBeLeftAtDoor || false,
    paymentMethod: d.billing?.paymentMethod || "invoice",
    yNumber: d.billing?.yNumber || "",
    companyName: d.billing?.companyName || "",
    paymentReference: d.billing?.paymentReference || "",
    billingDepartment: d.billing?.billingDepartment || "",
    selectedOption: null,
    busTransportData: null,
  };
}

/* ── Panel ── */

export function CreatePanel({ onClose, members = [], organizationData }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { saveDraftImmediate } = useDeliveryDraft(user?.uid);

  const [selectedMemberId, setSelectedMemberId] = useState(organizationData?.ownerId);
  const [creationMethod, setCreationMethod] = useState("text");

  const [pasteText, setPasteText] = useState("");
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteError, setPasteError] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const fileInputRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Members filtered to owner + senders for "create on behalf of"
  const senderMembers = [
    ...members.filter((m) => m.memberType === "any"),
    ...members.filter((m) => m.memberType === "sender"),
  ];

  const resetLocal = useCallback(() => {
    setPasteText("");
    setPasteLoading(false);
    setPasteError(null);
    setRecording(false);
    setAudioLoading(false);
    setAudioError(null);
    setRecordingDuration(0);
    setSelectedImage(null);
    setImagePreview(null);
    setImageLoading(false);
    setImageError(null);
    if (timerRef.current) clearInterval(timerRef.current);
    try { if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const saveDraftAndGo = (backendData) => {
    const draft = backendToDraft(backendData);
    if (selectedMemberId) draft.createdForMemberId = selectedMemberId;
    saveDraftImmediate(draft);
    resetLocal();
    onClose();
    const origin = encodeURIComponent(backendData.from || "");
    const destination = encodeURIComponent(backendData.to || "");
    let url = `/laheta-paketti?tab=send`;
    if (origin) url += `&origin=${origin}`;
    if (destination) url += `&destination=${destination}`;
    navigate(url);
  };

  const handlePasteSubmit = async () => {
    if (!pasteText.trim()) return;
    setPasteLoading(true);
    setPasteError(null);
    try {
      const result = await parseDeliveryTextFn(pasteText.trim());
      if (!result.success) throw new Error("Parse failed");
      saveDraftAndGo(result.data);
    } catch (err) {
      console.error("Paste parse error:", err);
      setPasteError("Tekstin käsittely epäonnistui. Yritä uudelleen.");
    } finally { setPasteLoading(false); }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setImageError("Valitse kuvatiedosto (JPG, PNG, etc.)"); return; }
    if (file.size > 10 * 1024 * 1024) { setImageError("Kuva on liian suuri. Maksimikoko on 10MB."); return; }
    setSelectedImage(file);
    setImageError(null);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleImageSubmit = async () => {
    if (!selectedImage) return;
    setImageLoading(true);
    setImageError(null);
    try {
      const base64 = await fileToBase64(selectedImage);
      const result = await parseDeliveryImageFn(base64, selectedImage.type);
      if (!result.success) throw new Error("Parse failed");
      saveDraftAndGo(result.data);
    } catch (err) {
      console.error("Image parse error:", err);
      setImageError("Kuvan käsittely epäonnistui. Yritä uudelleen.");
    } finally { setImageLoading(false); }
  };

  const handleImageClear = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startRecording = async () => {
    setAudioError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        await sendAudio(blob);
      };
      recorder.start();
      setRecording(true);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => setRecordingDuration((s) => s + 1), 1000);
    } catch { setAudioError("Mikrofoniin ei saatu yhteyttä. Tarkista käyttöoikeudet."); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state !== "inactive") { mediaRecorderRef.current.stop(); setRecording(false); }
  };

  const sendAudio = async (blob) => {
    setAudioLoading(true);
    setAudioError(null);
    try {
      const base64 = await blobToBase64(blob);
      const result = await parseDeliveryAudioFn(base64, blob.type || "audio/webm");
      if (!result.success) throw new Error("Parse failed");
      saveDraftAndGo(result.data);
    } catch (err) {
      console.error("Audio parse error:", err);
      setAudioError("Äänen käsittely epäonnistui. Yritä uudelleen.");
    } finally { setAudioLoading(false); }
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const handleSimpleCreate = () => {
    if (selectedMemberId) saveDraftImmediate({ createdForMemberId: selectedMemberId });
    resetLocal();
    onClose();
    navigate("/laheta-paketti?tab=send");
  };

  return (
    <Box>
      {/* ── Member Selection (owner + senders only) ── */}
      {organizationData && senderMembers.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Luo jäsenen nimissä
            </Typography>
            <FormControl fullWidth size="small">
              <Select value={selectedMemberId} onChange={(e) => setSelectedMemberId(e.target.value)} displayEmpty>
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">Ei valintaa</Typography>
                </MenuItem>
                {senderMembers.map((member) => (
                  <MenuItem key={member.userId} value={member.userId}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: "0.65rem" }}>
                        {(member.name || "?").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{member.name || "Nimetön"}</Typography>
                        {member.memberType === "owner" && (
                          <Typography variant="caption" color="text.secondary">Omistaja</Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* ── Creation Method Toggle ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>Miten luodaan?</Typography>
        <ToggleButtonGroup value={creationMethod} exclusive onChange={(e, val) => val && setCreationMethod(val)} fullWidth size="small"
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", py: 1, fontSize: "0.8rem" } }}>
          <ToggleButton value="text"><Type size={14} style={{ marginRight: 6 }} />Teksti</ToggleButton>
          <ToggleButton value="image"><ImageIcon size={14} style={{ marginRight: 6 }} />Kuva</ToggleButton>
          <ToggleButton value="audio"><Mic size={14} style={{ marginRight: 6 }} />Ääni</ToggleButton>
          <ToggleButton value="manual"><PlusCircle size={14} style={{ marginRight: 6 }} />Manuaali</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Text ── */}
      <Collapse in={creationMethod === "text"}>
        <Box>
          <TextField multiline rows={5} fullWidth placeholder="Liitä kuljetuksen tiedot tähän..." value={pasteText} onChange={(e) => setPasteText(e.target.value)} disabled={pasteLoading} sx={{ mb: 1.5 }} />
          {pasteError && <Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>{pasteError}</Typography>}
          <Button variant="contained" startIcon={pasteLoading ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            onClick={handlePasteSubmit} disabled={!pasteText.trim() || pasteLoading} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>
            {pasteLoading ? "Käsitellään..." : "Luo"}
          </Button>
        </Box>
      </Collapse>

      {/* ── Image ── */}
      <Collapse in={creationMethod === "image"}>
        <Box>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={{ display: "none" }} />
          {!selectedImage ? (
            <Button variant="outlined" startIcon={<Upload size={16} />} onClick={() => fileInputRef.current?.click()} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>Valitse kuva</Button>
          ) : (
            <Box>
              {imagePreview && (
                <Box sx={{ mb: 1.5, border: "2px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", maxHeight: 150, display: "flex", justifyContent: "center", bgcolor: "grey.50" }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain" }} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                {selectedImage.name} ({(selectedImage.size / 1024).toFixed(1)} KB)
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" startIcon={imageLoading ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                  onClick={handleImageSubmit} disabled={imageLoading} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>
                  {imageLoading ? "Käsitellään..." : "Luo"}
                </Button>
                <Button variant="outlined" onClick={handleImageClear} disabled={imageLoading} sx={{ textTransform: "none", fontWeight: 600, minWidth: 90 }}>Peruuta</Button>
              </Box>
            </Box>
          )}
          {imageError && <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>{imageError}</Typography>}
        </Box>
      </Collapse>

      {/* ── Audio ── */}
      <Collapse in={creationMethod === "audio"}>
        <Box>
          {!recording && !audioLoading ? (
            <Button variant="outlined" startIcon={<Mic size={16} />} onClick={startRecording} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>Aloita nauhoitus</Button>
          ) : recording ? (
            <Button variant="contained" color="error" startIcon={<MicOff size={16} />} onClick={stopRecording} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>
              Lopeta ({fmtTime(recordingDuration)})
            </Button>
          ) : (
            <Button variant="outlined" disabled startIcon={<CircularProgress size={16} />} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>Käsitellään...</Button>
          )}
          {audioError && <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>{audioError}</Typography>}
        </Box>
      </Collapse>

      {/* ── Manual ── */}
      <Collapse in={creationMethod === "manual"}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Luo kuljetus manuaalisesti täyttämällä kaikki tiedot itse.</Typography>
          <Button variant="contained" startIcon={<PlusCircle size={16} />} onClick={handleSimpleCreate} fullWidth sx={{ textTransform: "none", fontWeight: 600 }}>
            Jatka manuaaliseen luontiin
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}